import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Icon } from '@iconify/react'
import { X } from 'react-feather'

import {
  Spin,
  Button,
  Modal,
  Tooltip,
  useDocsContent,
  useTemplateInformation,
  useMessage,
} from '@pepper/ui'
import { generateHtmlFromJSON } from '@pepper/docs/src/utils/functions/htmlGeneratorFromJSON'

import { useSelectedCompanyId } from 'utils/functions/misc'

import { ReactComponent as TemplateIcon } from 'assets/images/template-icon.svg'

import styles from './TemplateViewerModal.module.css'

type TemplateViewerModalProps = {
  templateId: string
  onSubmit: (val: string | number, details?: string) => void
}

const TemplateViewerModal = NiceModal.create<TemplateViewerModalProps>(
  ({ templateId, onSubmit }) => {
    const { visible: isModalVisible, remove: removeModal } = useModal()
    const { showError } = useMessage()
    const companyId = useSelectedCompanyId()
    const { data: templateContent, isLoading: isLoadingContent } =
      useDocsContent({ data: { id: templateId, apiType: 'TEMPLATE' } })
    const {
      data: templateInformation,
      isLoading: isLoadingTemplateInformation,
    } = useTemplateInformation({ id: templateId })

    const templateCreatorName = templateInformation
      ? `${templateInformation.creator?.first_name} ${templateInformation.creator?.last_name}`
      : ''

    const handleSubmit = () => {
      if (!templateInformation) {
        showError('Template information unavailable')
        return
      }
      onSubmit(templateInformation.id, templateInformation.template_name)
      removeModal()
    }

    return (
      <Modal
        wrapClassName={styles.templateViewerModal}
        title={null}
        footer={null}
        visible={isModalVisible}
        onCancel={removeModal}
        width={864}
        destroyOnClose={true}
      >
        <div className={styles.modalTitle}>
          {!templateInformation || isLoadingTemplateInformation ? null : (
            <>
              <TemplateIcon className={styles.templateImageInList} />
              <span
                className={styles.templateName}
                title={templateInformation.template_name}
              >
                {templateInformation.template_name}
              </span>
              <span className={styles.divider}></span>
              <span
                title={templateCreatorName}
                className={styles.templateDetails}
              >
                Created by {templateCreatorName}
              </span>
              <div className={styles.actions}>
                <div
                  className={styles.extLink}
                  role="none"
                  onClick={() =>
                    window.open(`/${companyId}/template/${templateId}`)
                  }
                >
                  <Icon
                    icon="tabler:external-link"
                    className={styles.extIcon}
                  />{' '}
                  Open in docs
                </div>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  className={styles.submitButton}
                >
                  Use this template
                </Button>
              </div>
              <Tooltip placement="bottom" title="Close">
                <X
                  size={16}
                  onClick={removeModal}
                  className={styles.closeIcon}
                />
              </Tooltip>
            </>
          )}
        </div>

        <div className={styles.modalBody}>
          {!templateContent || isLoadingContent ? (
            <Spin className={styles.spinner} />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: generateHtmlFromJSON(templateContent?.content.json),
              }}
            />
          )}
        </div>
      </Modal>
    )
  }
)

export default TemplateViewerModal
